import { Spinner } from "@/components/ui/spinner";
import { useState, useEffect, useRef } from "react";
import { ExpenseCategory, type CategoryBudgetItem } from "@boo/shared";
import { CATEGORY_LABELS } from "../../monthly-summary/types/category.types";
import { useBudgetStore } from "../store/useBudgetStore";
import AmountInput from "@/components/AmountInput";

function ProjectionHint({
  projectedAmount,
  monthsOfHistory,
}: {
  projectedAmount: number | undefined;
  monthsOfHistory: number | undefined;
}) {
  if (projectedAmount === undefined) return null;
  const fmt = projectedAmount.toLocaleString("th-TH", {
    minimumFractionDigits: 0,
  });
  const lowConfidence = monthsOfHistory === 1;
  return (
    <span className="text-[11px] text-muted-foreground [font-variant-numeric:tabular-nums]">
      Projected: ฿{fmt}
      {lowConfidence && <span className="ml-1 italic">(low confidence)</span>}
    </span>
  );
}

const ALL_BUDGETABLE_CATEGORIES = Object.values(ExpenseCategory);

interface CategoryRow {
  category: ExpenseCategory;
  inputValue: string;
  error: string | null;
}

export default function CategoryBudgetEditor() {
  const {
    budget,
    projections,
    saving,
    upsertCategoryBudgets,
    pendingAddCategory,
    setPendingAddCategory,
  } = useBudgetStore();
  const [rows, setRows] = useState<CategoryRow[]>([]);
  const [saved, setSaved] = useState(false);
  const [sumError, setSumError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [prevBudget, setPrevBudget] = useState(budget);
  if (prevBudget !== budget) {
    setPrevBudget(budget);
    setRows(
      budget?.categoryBudgets.map((c) => ({
        category: c.category,
        inputValue: String(c.amount),
        error: null,
      })) ?? [],
    );
  }

  const [prevPending, setPrevPending] = useState<ExpenseCategory | null>(null);
  if (pendingAddCategory && prevPending !== pendingAddCategory) {
    setPrevPending(pendingAddCategory);
    if (!rows.some((r) => r.category === pendingAddCategory)) {
      setRows([
        ...rows,
        { category: pendingAddCategory, inputValue: "", error: null },
      ]);
    }
  }

  useEffect(() => {
    if (!pendingAddCategory) return;
    setPendingAddCategory(null);
    setTimeout(
      () =>
        containerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        }),
      50,
    );
  }, [pendingAddCategory, setPendingAddCategory]);

  function addCategory(category: ExpenseCategory) {
    setRows((prev) => [...prev, { category, inputValue: "", error: null }]);
  }

  function removeCategory(category: ExpenseCategory) {
    setRows((prev) => prev.filter((r) => r.category !== category));
    if (sumError) setSumError(null);
  }

  function handleAmountChange(category: ExpenseCategory, value: string) {
    setRows((prev) =>
      prev.map((r) =>
        r.category === category ? { ...r, inputValue: value, error: null } : r,
      ),
    );
    if (saved) setSaved(false);
    if (sumError) setSumError(null);
  }

  async function handleSave() {
    let hasError = false;
    const validated = rows.map((r) => {
      const amount = parseFloat(r.inputValue);
      if (!r.inputValue || isNaN(amount) || amount <= 0) {
        hasError = true;
        return { ...r, error: "Must be greater than 0" };
      }
      return { ...r, error: null };
    });
    setRows(validated);
    if (hasError) return;

    const overallAmount = budget?.overallAmount ?? 0;
    const categorySum = validated.reduce(
      (s, r) => s + parseFloat(r.inputValue),
      0,
    );
    if (categorySum > overallAmount) {
      const excess = categorySum - overallAmount;
      setSumError(
        `Category budgets exceed your overall budget by ฿${excess.toLocaleString("th-TH", { minimumFractionDigits: 0 })}`,
      );
      return;
    }
    setSumError(null);

    const items: CategoryBudgetItem[] = validated.map((r) => ({
      category: r.category,
      amount: parseFloat(r.inputValue),
    }));

    try {
      await upsertCategoryBudgets(items);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // error shown from store
    }
  }

  const projectionMap = new Map(
    (projections?.categories ?? []).map((p) => [
      p.category,
      {
        projectedAmount: p.projectedAmount,
        monthsOfHistory: p.monthsOfHistory,
      },
    ]),
  );

  const budgetedCategories = new Set(rows.map((r) => r.category));
  const untrackedCategories = ALL_BUDGETABLE_CATEGORIES.filter(
    (c) => !budgetedCategories.has(c),
  );

  return (
    <div
      ref={containerRef}
      className="rounded-2xl bg-card border border-border p-4 sm:p-6 shadow-card"
    >
      <h2 className="text-[15px] font-semibold text-foreground mb-1">
        Category Budgets
      </h2>
      <p className="text-[13px] text-muted-foreground mb-5">
        Set limits per spending category.
      </p>

      {rows.length > 0 && (
        <div className="flex flex-col gap-3 mb-4">
          {rows.map((row) => (
            <div key={row.category} className="flex items-start gap-2">
              <span className="text-[13px] font-medium text-foreground w-20 truncate sm:w-28 sm:min-w-[7rem] flex-shrink-0 pt-2.5">
                {CATEGORY_LABELS[row.category]}
              </span>
              <div className="flex-1 min-w-0 flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-medium text-muted-foreground flex-shrink-0">
                    ฿
                  </span>
                  <AmountInput
                    value={row.inputValue}
                    onChange={(v) => handleAmountChange(row.category, v)}
                    placeholder="e.g. 5000"
                    className={`flex-1 min-w-0 rounded-xl border px-4 py-2 text-[14px] bg-background text-foreground outline-none transition-colors [font-variant-numeric:tabular-nums] ${
                      row.error
                        ? "border-destructive focus:ring-1 focus:ring-destructive"
                        : "border-border focus:border-primary focus:ring-1 focus:ring-primary"
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => removeCategory(row.category)}
                    className="text-muted-foreground hover:text-destructive transition-colors text-[13px] px-2 py-1 rounded-lg hover:bg-destructive/10 flex-shrink-0"
                    aria-label={`Remove ${CATEGORY_LABELS[row.category]}`}
                  >
                    ✕
                  </button>
                </div>
                {(row.error || projectionMap.get(row.category)) && (
                  <div className="flex gap-3 pl-5">
                    {row.error && (
                      <p className="text-[12px] text-destructive">
                        {row.error}
                      </p>
                    )}
                    <ProjectionHint
                      projectedAmount={
                        projectionMap.get(row.category)?.projectedAmount
                      }
                      monthsOfHistory={
                        projectionMap.get(row.category)?.monthsOfHistory
                      }
                    />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {sumError && (
        <p className="text-[12px] text-destructive mb-3">{sumError}</p>
      )}

      {rows.length > 0 && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center justify-center gap-1.5 self-start px-5 py-2 rounded-xl bg-primary text-primary-foreground text-[13px] font-medium transition-opacity hover:opacity-90 disabled:opacity-50 mb-6"
        >
          {saving && <Spinner className="size-3.5" />}
          {saving ? "Saving…" : saved ? "Saved!" : "Save categories"}
        </button>
      )}

      {untrackedCategories.length > 0 && (
        <div className="border-t border-border pt-5">
          <p className="text-[12px] font-medium text-muted-foreground mb-3 uppercase tracking-wide">
            Untracked categories
          </p>
          <div className="flex flex-wrap gap-2">
            {untrackedCategories.map((cat) => {
              const proj = projectionMap.get(cat);
              const lowConfidence = proj?.monthsOfHistory === 1;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => addCategory(cat)}
                  className="flex flex-col items-start px-3 py-1.5 rounded-xl border border-border text-[13px] text-foreground bg-background hover:bg-muted transition-colors"
                >
                  <span>+ {CATEGORY_LABELS[cat]}</span>
                  {proj !== undefined ? (
                    <span className="text-[11px] text-muted-foreground [font-variant-numeric:tabular-nums]">
                      ฿
                      {proj.projectedAmount.toLocaleString("th-TH", {
                        minimumFractionDigits: 0,
                      })}{" "}
                      projected
                      {lowConfidence && (
                        <span className="ml-1 italic">(low confidence)</span>
                      )}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {rows.length === 0 && untrackedCategories.length === 0 && (
        <p className="text-[13px] text-muted-foreground">
          All categories budgeted.
        </p>
      )}
    </div>
  );
}
